import React, { memo, useCallback, useContext, useEffect, useState } from 'react'
import {
  Typography,
  Button,
  Box,
  IconButton,
  DialogActions,
  Autocomplete,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
  InputLabel,
  FormControlLabel,
  Checkbox,
  InputAdornment
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { AnuncioDTO, IAnunciante, ITipoAnuncio } from '../types/anuncios.types'
import Dropzone from '../../Products/components/Dropzone'
import { ClearIcon, DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import DictionaryItem from '../../../dto/DictionaryDTO'
import { TenantsContext } from '../../../context/tenants.context'
import { crearAnuncio, updateAnuncio } from '../services/services.anuncios'
import SnackBarUtils from '../../../utils/SnackBarUtils'
import AddFoto from './addFoto'
import { Rubro } from '../../Rubros/AbmRubros/types/rubro'

interface FormAnuncioProps {
  open: boolean
  setOpen: (op: boolean) => void
  anunciantes: IAnunciante[]
  tiposAnuncio: ITipoAnuncio[]
  accountTypes: DictionaryItem[]
  rubros: Rubro[]
  anuncio?: AnuncioDTO
  setAnuncio?: (a: AnuncioDTO) => void
  setSubmit: (submit: boolean) => void
  isMobile: boolean
}

const MAX_NOMBRE_LENGTH = 45
const MAX_TEXTO_LENGTH = 1000
const MAX_LINK_LENGTH = 500

const FormAnuncio: React.FC<FormAnuncioProps> = ({
  open,
  setOpen,
  anunciantes,
  tiposAnuncio,
  accountTypes,
  rubros,
  anuncio,
  setAnuncio,
  setSubmit,
  isMobile
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)
  const [newAnuncio, setNewAnuncio] = useState<AnuncioDTO>(null)
  const [fechaDesde, setFechaDesde] = useState<any>(null)
  const [fechaHasta, setFechaHasta] = useState<any>(null)
  const [images, setImages] = useState<any[]>([])
  const [dropError, setDropError] = useState<string>(null)
  const [foto, setFoto] = useState<any>(null)
  const [fotoWide, setFotoWide] = useState<any>(null)
  const [codesRubros, setCodesRubros] = useState<string[]>([])
  const [idsTiposCuenta, setIdsTiposCuenta] = useState<number[]>([])
  const [medidas, setMedidas] = useState<string>(null)
  const [medidasWide, setMedidasWide] = useState<string>(null)
  const { tenant } = useContext(TenantsContext)

  const schema = yup.object({
    nombre: yup.string().required('El nombre es obligatorio'),
    tipo: yup.number().required('Elija un tipo de anuncio'),
    anunciante: yup.number().required('Elija un anunciante')
  })

  const onSubmit = useCallback(
    async (data: any) => {
      if (foto === null) return SnackBarUtils.error('Debes agregar una imagen')
      const { activo, ...anuncioSinActivo } = newAnuncio

      const anuncio: AnuncioDTO = {
        ...anuncioSinActivo,
        codRubroList: codesRubros,
        cuentaTipoCSV: idsTiposCuenta?.length > 0 ? idsTiposCuenta.join(',') : null,
        imagenBase64: foto?.startsWith('data:') ? foto : null,
        imagenWideBase64: fotoWide?.startsWith('data:') ? fotoWide : null,
        fechaDesde: fechaDesde ? dayjs(fechaDesde).format('YYYY-MM-DD') : null,
        fechaHasta: fechaHasta ? dayjs(fechaHasta).format('YYYY-MM-DD') : null,
        borrarImagenWide: !fotoWide,
        detalleImagenList:
          images?.map((image) => {
            // Verificar si la imagen ya existía en el anuncio original
            const existeEnOriginal = newAnuncio?.detalleImagenList?.some((img) => img.url === image.url)

            return {
              url: image.url || null,
              uploadBase64: !existeEnOriginal && image.base64 ? image.base64 : null,
              desc: image.cta
            }
          }) || null
      }
      setLoading(true)
      try {
        editing ? await updateAnuncio(tenant, newAnuncio?.idAnuncio, anuncio) : await crearAnuncio(tenant, anuncio)
        SnackBarUtils.success('Anuncio guardado')
        setSubmit(true)
      } catch (e) {
        console.log(e)
      } finally {
        setLoading(false)
        handleClose()
      }
    },
    [newAnuncio, tenant, idsTiposCuenta, codesRubros, foto, fotoWide, fechaDesde, fechaHasta, images]
  )

  useEffect(() => {
    if (anuncio) {
      const tipoAnuncio = tiposAnuncio.find(
        (t: { id: number; nombre: string; medidas: string }) => t.id === anuncio.idAnuncioTipo
      )
      setEditing(true)
      setNewAnuncio(anuncio)
      setValue('nombre', anuncio.nombre)
      setValue('tipo', anuncio.idAnuncioTipo)
      setValue('anunciante', anuncio.idAnunciante)
      setFechaDesde(anuncio.fechaDesde ? dayjs(anuncio.fechaDesde) : null)
      setFechaHasta(anuncio.fechaHasta ? dayjs(anuncio.fechaHasta) : null)
      setImages(anuncio.detalleImagenList || [])
      setFoto(anuncio.urlImagen)
      setFotoWide(anuncio.urlImagenWide)
      setCodesRubros(anuncio.codRubroList || [])
      setIdsTiposCuenta(anuncio.cuentaTipoCSV ? anuncio.cuentaTipoCSV.split(',').map((id) => Number(id.trim())) : [])
      tipoAnuncio.medidas && setMedidas(tipoAnuncio.medidas)
      tipoAnuncio.medidasWide && setMedidasWide(tipoAnuncio.medidasWide)
    }
  }, [anuncio])

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  })

  const handleClose = () => {
    setOpen(false)
    setAnuncio(null)
    setNewAnuncio(null)
    setFoto(null)
    setFotoWide(null)
    setEditing(false)
    setFechaDesde(null)
    setFechaHasta(null)
    setMedidas(null)
    setMedidasWide(null)
    setImages([])
    setCodesRubros([])
    setIdsTiposCuenta([])
    reset()
  }

  if (editing && !newAnuncio) return
  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            overflowX: 'hidden'
          }
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{anuncio ? 'Editar Anuncio' : 'Crear Anuncio'}</DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Stack sx={{ width: '100%' }} spacing={1}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                Nombre
                <Typography variant="subtitle2">
                  {newAnuncio?.nombre?.length || 0}/{MAX_NOMBRE_LENGTH}
                </Typography>
              </InputLabel>
              <TextField
                {...register('nombre')}
                value={newAnuncio?.nombre}
                onChange={(e) => setNewAnuncio({ ...newAnuncio, nombre: e.target.value })}
                variant="outlined"
                disabled={loading}
                sx={{ width: '100%' }}
                error={!!errors.nombre}
                helperText={errors?.nombre && String(errors?.nombre?.message)}
                inputProps={{ maxLength: MAX_NOMBRE_LENGTH }}
              />
            </Stack>
            <Autocomplete
              options={anunciantes}
              getOptionLabel={(a) => a.nombre || ''}
              onChange={(_, value) => {
                setValue('anunciante', value?.idAnunciante)
                setNewAnuncio({
                  ...newAnuncio,
                  idAnunciante: anunciantes.find((a) => a.idAnunciante === value?.idAnunciante)?.idAnunciante || null
                })
              }}
              disabled={loading}
              value={anunciantes?.find((a) => a.idAnunciante === newAnuncio?.idAnunciante) || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Anunciante"
                  placeholder=""
                  variant="outlined"
                  error={!!errors.anunciante}
                  helperText={errors?.anunciante && String(errors?.anunciante?.message)}
                />
              )}
              renderOption={(props, a: { idAnunciante: number; idLocal: number; nombre: string }) => (
                <MenuItem {...props} value={a.idAnunciante} key={a.idAnunciante}>
                  {`${a.nombre} (${a.idLocal})`}
                </MenuItem>
              )}
            />
            <Autocomplete
              options={tiposAnuncio}
              getOptionLabel={(t) => t.nombre || ''}
              onChange={(_, value) => {
                setValue('tipo', value?.id)
                setMedidas(value?.medidas)
                setMedidasWide(value?.medidasWide)
                setNewAnuncio({
                  ...newAnuncio,
                  idAnuncioTipo: tiposAnuncio.find((t) => t.id === value?.id)?.id || null
                })
              }}
              disabled={loading}
              value={tiposAnuncio?.find((t) => t.id === newAnuncio?.idAnuncioTipo) || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tipos de Anuncio"
                  placeholder=""
                  variant="outlined"
                  error={!!errors.tipo}
                  helperText={errors?.tipo && String(errors?.tipo?.message)}
                />
              )}
              renderOption={(props, t: { id: number; nombre: string; medidas: string }) => (
                <MenuItem {...props} value={t.id} key={t.id}>
                  {t.nombre}
                </MenuItem>
              )}
            />
            <Stack sx={{ width: '100%' }} spacing={1}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                Imagen {medidas && `(medidas: ${medidas})`}
              </InputLabel>
              <AddFoto foto={foto} setFoto={setFoto} setDropError={setDropError} />
            </Stack>
            <Stack sx={{ width: '100%' }} spacing={1}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                Imagen Wide {medidasWide && `(medidas: ${medidasWide})`}
              </InputLabel>
              <AddFoto foto={fotoWide} setFoto={setFotoWide} setDropError={setDropError} medidas={medidas} />
            </Stack>
            <Stack sx={{ width: '100%' }} spacing={1}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                Detalle Texto
                <Typography variant="subtitle2">
                  {newAnuncio?.detalleTexto?.length || 0}/{MAX_TEXTO_LENGTH}
                </Typography>
              </InputLabel>
              <TextField
                value={newAnuncio?.detalleTexto}
                onChange={(e) => setNewAnuncio({ ...newAnuncio, detalleTexto: e.target.value })}
                variant="outlined"
                disabled={loading}
                sx={{ width: '100%' }}
                inputProps={{ maxLength: MAX_TEXTO_LENGTH }}
              />
            </Stack>
            <Stack sx={{ width: '100%' }} spacing={1}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                Detalle Link
                <Typography variant="subtitle2">
                  {newAnuncio?.detalleLink?.length || 0}/{MAX_LINK_LENGTH}
                </Typography>
              </InputLabel>
              <TextField
                value={newAnuncio?.detalleLink}
                onChange={(e) => setNewAnuncio({ ...newAnuncio, detalleLink: e.target.value })}
                variant="outlined"
                disabled={loading}
                sx={{ width: '100%' }}
                inputProps={{ maxLength: MAX_LINK_LENGTH }}
              />
            </Stack>
            <Stack sx={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={loading}
                  label="Fecha Desde"
                  format="DD/MM/YYYY"
                  value={fechaDesde}
                  onChange={(date: any) => {
                    setFechaDesde(date || null)
                  }}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      value: fechaDesde,
                      ...(fechaDesde && {
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setFechaDesde(null)}
                                disabled={loading}
                                edge="end"
                              >
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      })
                    }
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disabled={loading}
                  label="Fecha Hasta"
                  format="DD/MM/YYYY"
                  value={fechaHasta}
                  onChange={(date: any) => {
                    setFechaHasta(date || null)
                  }}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      value: fechaHasta,
                      ...(fechaHasta && {
                        InputProps: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => setFechaHasta(null)}
                                disabled={loading}
                                edge="end"
                              >
                                <ClearIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      })
                    }
                  }}
                />
              </LocalizationProvider>
            </Stack>
            {[2, 3].includes(newAnuncio?.idAnuncioTipo) && (
              <Stack sx={{ width: '100%', display: 'flex', flexDirection: 'row' }} spacing={1}>
                <Autocomplete
                  disabled={newAnuncio?.compradorTodosLosRubros}
                  multiple
                  options={rubros}
                  getOptionLabel={(option) => option.nombreAnalytics}
                  value={rubros.filter((r) => codesRubros?.includes(r.codRubro))}
                  onChange={(_, value) => {
                    setCodesRubros(value.map((r) => r.codRubro))
                  }}
                  sx={{ width: '80%' }}
                  renderInput={(params) => <TextField {...params} label="Seleccioná rubros" />}
                />
                <FormControlLabel
                  sx={{ whiteSpace: 'nowrap', width: '20%' }}
                  control={
                    <Checkbox
                      checked={newAnuncio?.compradorTodosLosRubros}
                      onChange={(e) => {
                        setNewAnuncio({ ...newAnuncio, compradorTodosLosRubros: e.target.checked })
                        setCodesRubros([])
                      }}
                    />
                  }
                  label="Todos"
                />
              </Stack>
            )}
            {[1, 4, 5].includes(newAnuncio?.idAnuncioTipo) && (
              <Autocomplete
                options={accountTypes}
                multiple
                getOptionLabel={(a) => a.nombre || ''}
                value={accountTypes.filter((t) => idsTiposCuenta?.includes(t.id))}
                onChange={(_, value) => {
                  setIdsTiposCuenta(value.map((t) => t.id))
                }}
                disabled={loading}
                renderInput={(params) => (
                  <TextField {...params} label="Tipo de cuenta" placeholder="" variant="outlined" />
                )}
                renderOption={(props, t: { id: number; nombre: string }) => (
                  <MenuItem {...props} value={t.id} key={t.id}>
                    {t.nombre}
                  </MenuItem>
                )}
              />
            )}
            <Stack sx={{ width: '100%' }} spacing={1}>
              <InputLabel
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%'
                }}
              >
                Detalle Imágenes
              </InputLabel>
              <Dropzone images={images} setImages={setImages} cta={true} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <IconButton
              disabled={loading}
              edge="end"
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '0',
                right: '0',
                paddingRight: '16px'
              }}
            >
              <CloseIcon />
            </IconButton>
            <Button
              disabled={loading}
              variant="contained"
              color="error"
              onClick={handleClose}
              sx={{ marginTop: '20px', width: '100%' }}
            >
              Cancelar
            </Button>
            <Button
              disabled={loading}
              variant="contained"
              color="success"
              sx={{ marginTop: '20px', width: '100%' }}
              type="submit"
            >
              Guardar
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      {dropError && (
        <Dialog
          open={dropError !== null || dropError !== undefined}
          onClose={() => setDropError(undefined)}
          maxWidth="lg"
        >
          <DialogTitle>Errores en la subida de imagenes</DialogTitle>
          <DialogContent>{dropError}</DialogContent>
          <DialogActions>
            <Button onClick={() => setDropError(undefined)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default memo(FormAnuncio)
